import Cookies from "js-cookie";
import { hrefFromUrl, isProgramDayHash } from "./program-url";
let shortlistedIds: string[] = [];

function initFilter() {
  const programFilter = document.querySelector(".program-filters");
  if (!programFilter) return;

  const HIGHLIGHTS_KEY = "types";

  const highlightFilters = programFilter.querySelectorAll(
    `input[name="${HIGHLIGHTS_KEY}[]"]`
  ) as NodeListOf<HTMLInputElement>;
  const genreFilters = programFilter.querySelectorAll(
    `[data-filter-group="genre"] input[name="${HIGHLIGHTS_KEY}[]"]`
  ) as NodeListOf<HTMLInputElement>;
  const accessFilters = programFilter.querySelectorAll(
    `[data-filter-group="access"] input[name="${HIGHLIGHTS_KEY}[]"]`
  ) as NodeListOf<HTMLInputElement>;

  function getCheckedValues(inputs: NodeListOf<HTMLInputElement>): string[] {
    return Array.from(inputs)
      .filter((input) => input.checked)
      .map((input) => input.value);
  }

  function syncState() {
    const values = getCheckedValues(highlightFilters);
    Cookies.set(HIGHLIGHTS_KEY, values.join(","));

    const next = new URL(window.location.href);
    const dailyTab = document.getElementById("daily");
    const onDailyTab = dailyTab?.getAttribute("aria-selected") === "true";

    if (onDailyTab || isProgramDayHash()) {
      next.searchParams.delete(HIGHLIGHTS_KEY);
      if (!next.hash) {
        next.hash = "program";
      }
    } else {
      if (values.length > 0) {
        next.searchParams.set(HIGHLIGHTS_KEY, values.join(","));
      } else {
        next.searchParams.delete(HIGHLIGHTS_KEY);
      }
      if (!next.hash || isProgramDayHash(next.hash)) {
        next.hash = "program";
      }
    }

    window.history.replaceState(null, document.title, hrefFromUrl(next));

    filterShowList();
    updateAdsVisibility(values.length > 0);
    updateClearButtons();
  }

  highlightFilters.forEach((input) => {
    input.addEventListener("change", () => {
      syncState();
    });

    input.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        input.click();
      }
    });
  });

  programFilter.querySelectorAll<HTMLButtonElement>("[data-clear]").forEach((button) => {
    button.addEventListener("click", () => {
      const group = button.getAttribute("data-clear");
      const groupInputs = programFilter.querySelectorAll<HTMLInputElement>(
        `[data-filter-group="${group}"] input[name="${HIGHLIGHTS_KEY}[]"]`
      );
      groupInputs.forEach((input) => {
        input.checked = false;
      });
      syncState();
    });
  });

  const savedHighlights = Cookies.get(HIGHLIGHTS_KEY)?.split(",") || [""];
  const urlHasTypes = Boolean(new URLSearchParams(window.location.search).get("types")?.trim());

  if (savedHighlights[0] !== "") {
    highlightFilters.forEach((input) => {
      input.checked = savedHighlights.includes(input.value);
    });
    if (urlHasTypes || !isProgramDayHash()) {
      syncState();
    } else {
      filterShowList();
      updateAdsVisibility(getCheckedValues(highlightFilters).length > 0);
      updateClearButtons();
    }
  } else {
    filterShowList();
    updateAdsVisibility(getCheckedValues(highlightFilters).length > 0);
    updateClearButtons();
  }

  function filterShowList() {
    const showList = document.querySelector(".show-list") as HTMLElement;
    if (!showList) return;

    const showItems = showList.querySelectorAll(".show-list-item");
    const selectedGenre = getCheckedValues(genreFilters);
    const selectedAccess = getCheckedValues(accessFilters);

    showItems.forEach((item) => {
      if (item.classList.contains("show-list-item--ad")) {
        return;
      }

      const itemHighlights = item.getAttribute("data-highlights")?.split(",") || [];
      const genreMatches =
        selectedGenre.length === 0 || selectedGenre.some((slug) => itemHighlights.includes(slug));
      const accessMatches =
        selectedAccess.length === 0 ||
        selectedAccess.some((slug) => itemHighlights.includes(slug));

      if (genreMatches && accessMatches) {
        item.classList.remove("show-list-item--hidden");
      } else {
        item.classList.add("show-list-item--hidden");
      }
    });

    updateShowlistWithShortlist();
    updateFilterResultsCount();
  }

  function updateClearButtons() {
    programFilter.querySelectorAll<HTMLButtonElement>("[data-clear]").forEach((button) => {
      const group = button.getAttribute("data-clear");
      const groupInputs = programFilter.querySelectorAll<HTMLInputElement>(
        `[data-filter-group="${group}"] input[name="${HIGHLIGHTS_KEY}[]"]`
      );
      const hasSelection = getCheckedValues(groupInputs).length > 0;
      const item = button.closest(".program-filters-clear-item") as HTMLElement | null;

      button.hidden = !hasSelection;
      if (item) {
        item.hidden = !hasSelection;
      }
    });
  }

  function updateFilterResultsCount() {
    const results = document.querySelector(".program-filter-results") as HTMLElement | null;
    if (!results) return;

    const hasFilters =
      getCheckedValues(genreFilters).length > 0 || getCheckedValues(accessFilters).length > 0;

    if (!hasFilters) {
      results.hidden = true;
      results.textContent = "";
      return;
    }

    const showList = document.querySelector(".show-list");
    const count = showList
      ? showList.querySelectorAll(".show-list-item:not(.show-list-item--ad):not(.show-list-item--hidden)")
          .length
      : 0;
    const resultLabel = count === 1 ? "result" : "results";

    results.hidden = false;
    results.textContent = `Showing ${count} ${resultLabel} from filters`;
  }

  const shortlistCheckbox = programFilter.querySelector("#shortlist") as HTMLInputElement;
  if (shortlistCheckbox) {
    if (shortlistedIds.length === 0) {
      shortlistCheckbox.disabled = true;
    } else {
      shortlistCheckbox.disabled = false;
      updateShowlistWithShortlist();
    }

    shortlistCheckbox.addEventListener("change", () => {
      if (shortlistCheckbox.checked) {
        updateShowlistWithShortlist();
      } else {
        filterShowList();
      }
    });
  }

  document.addEventListener("shortlist:updated", (event: Event) => {
    shortlistedIds = (event as CustomEvent<{slugs: string[]}>).detail.slugs;
    if (shortlistCheckbox) {
      shortlistCheckbox.disabled = shortlistedIds.length === 0;
      if (!shortlistedIds.length) shortlistCheckbox.checked = false;
    }
    filterShowList();
  });

  // Show ads
  function updateAdsVisibility(isHidden: boolean) {
    const showList = document.querySelector("ul.show-list") as HTMLElement;
    if (showList) {
      if (isHidden) {
        showList.classList.add("no-ads");
      } else {
        showList.classList.remove("no-ads");
      }
    }
  }
}

initFilter();

export function updateShowlistWithShortlist() {
  const programFilter = document.querySelector(".program-filters");
  const shortlistCheckbox = programFilter?.querySelector("#shortlist") as HTMLInputElement;
  const showList = document.querySelector(".show-list") as HTMLElement;
  if (!showList) return;
  const showItems = showList.querySelectorAll(".show-list-item");

  showItems.forEach((item) => {
    const showId = item.getAttribute("id");
    if (
      shortlistCheckbox &&
      shortlistCheckbox.checked &&
      shortlistedIds.includes(showId?.replace("item-", "") || "")
    ) {
      item.classList.remove("show-list-item--hidden");
    }
  });
}
